import {
  LabelId,
  MessagingPlatform,
  UserCommunication,
  ViewType,
} from '@this+that/models';
import { calculateViews } from '@this+that/utils';
import { StoreApi } from 'zustand';

import {
  EMAIL_PLATFORM_SET,
  isEmailRowOfRemovedAccount,
} from './connectedAccountRows';
import { labelStateKey, viewTypeToStateKey } from './helpers';
import { UserCommunicationsStore } from './types';
import { LABEL_PREFIXES } from '../../../components/InboxLabelsTabsFilter/constants';
import { VIEW_TYPES } from '../../../enums/viewTypes';
import {
  CONFIRMATION_CLOCK_SKEW_MS,
  UNCONFIRMED_DRAFT_TTL_MS,
} from '../../../utils/constants/drafts';
import {
  buildThreadRestoreUserCommunication,
  isDraft,
  isFreshUnconfirmedDraft,
  resolveServerRowDraftId,
  rowCarriesDraft,
  rowCarriesMessage,
  stripThreadCarriedDraft,
} from '../../../utils/userCommunicationHelpers';
import { indexedDBAdapter } from '../../utils/indexedDBAdapter';
import {
  ReconcileCommunicationResult,
  useCommunicationIntentsStore,
} from '../CommunicationIntents';
import { findActiveLabelsIntentFor } from '../CommunicationIntents/handlers';
import { useIntegrationsStore } from '../Integrations';

type ZustandSet = StoreApi<UserCommunicationsStore>['setState'];
type ZustandGet = StoreApi<UserCommunicationsStore>['getState'];

// Shared slice-key format for a `labels[key]` list, distinguishing it from a
// fixed category name (`primary`, `draft`, …) in the sliceKeys arrays this
// file hands back to callers (see replaceRowInSlices).
const LABEL_SLICE_KEY_PREFIX = 'labels.';

// Gates the three single-row WebSocket insert paths only — never MODIFY (the
// server's MODIFY frame omits userEmailAddress, an envelope-sealed field) and
// never a page save (see isEmailRowOfRemovedAccount, applied there instead).
const shouldFilterCommunication = (
  communication: UserCommunication,
): boolean => {
  if (
    !communication.platform ||
    !EMAIL_PLATFORM_SET.has(communication.platform)
  ) {
    return true;
  }
  // No address to judge it by — same carve-out as isEmailRowOfRemovedAccount,
  // so a hand-built row is never silently dropped.
  if (!communication.userEmailAddress) {
    return true;
  }

  const { currentEmailFilters, isEmailAccountRemoved } =
    useIntegrationsStore.getState();

  if (currentEmailFilters !== undefined) {
    return currentEmailFilters.includes(communication.userEmailAddress);
  }

  return !isEmailAccountRemoved(
    communication.platform,
    communication.userEmailAddress,
  );
};

export const handleSetLoading = (set: ZustandSet, isLoading: boolean) => {
  set({ isLoading });
};

export const handleMarkViewTypesSynced = (
  set: ZustandSet,
  viewTypes: ViewType[],
) => {
  set((state) => ({
    syncedViewTypes: Array.from(
      new Set([...state.syncedViewTypes, ...viewTypes]),
    ),
  }));
};

export const handleSetCreateLoading = (
  set: ZustandSet,
  createLoading: boolean,
) => {
  set({ createLoading });
};

export const handleSetForwardLoading = (
  set: ZustandSet,
  forwardLoading: boolean,
) => {
  set({ forwardLoading });
};

export const handleSetReplyLoading = (
  set: ZustandSet,
  replyLoading: boolean,
) => {
  set({ replyLoading });
};

export const handleSetDeleteLoading = (
  set: ZustandSet,
  deleteLoading: boolean,
) => {
  set({ deleteLoading });
};

export const handleSetArchiveLoading = (
  set: ZustandSet,
  archiveLoading: boolean,
) => {
  set({ archiveLoading });
};

export const handleSetMoveToInboxLoading = (
  set: ZustandSet,
  moveToInboxLoading: boolean,
) => {
  set({ moveToInboxLoading });
};

export const handleSetMarkAsReadLoading = (
  set: ZustandSet,
  markAsReadLoading: boolean,
) => {
  set({ markAsReadLoading });
};

export const handleSetMarkAsUnreadLoading = (
  set: ZustandSet,
  markAsUnreadLoading: boolean,
) => {
  set({ markAsUnreadLoading });
};

export const handleSaveAllUserCommunications = (
  set: ZustandSet,
  userCommunications: UserCommunication[],
  exclusiveStartKey: string | null,
  loadMore?: boolean,
) => {
  const now = Date.now();
  const { isEmailAccountRemoved } = useIntegrationsStore.getState();
  const filteredCommunications = userCommunications.filter(
    (row) => !isEmailRowOfRemovedAccount(isEmailAccountRemoved)(row),
  );

  set((state) => {
    const {
      communications: draftReconciled,
      unconfirmedDrafts,
      deletedDrafts,
      lastDeletedDraftSliceKeys,
    } = reconcileUnconfirmedDrafts(filteredCommunications, state, now);

    const activeCommunications = filterReconciledForViewType(
      draftReconciled.map((communication) =>
        useCommunicationIntentsStore
          .getState()
          .reconcileCommunication(communication),
      ),
      [VIEW_TYPES.ALL_EMAILS],
    );

    const currentAll = loadMore ? [...state.all] : [];

    const upsert = (arr: UserCommunication[], item: UserCommunication) => {
      const map = new Map(arr.map((msg) => [msg.messageId, msg]));
      map.set(item.messageId, item);
      return Array.from(map.values());
    };

    let newAll = [...currentAll];

    activeCommunications.forEach((communication) => {
      newAll = upsert(newAll, communication);
    });

    if (!loadMore) {
      newAll = reinjectLocalOnlyRows(
        state.all,
        newAll,
        [VIEW_TYPES.ALL_EMAILS],
        unconfirmedDrafts,
        now,
      );
    }

    return {
      ...state,
      all: newAll,
      unconfirmedDrafts,
      deletedDrafts,
      lastDeletedDraftSliceKeys,
      startKeys: {
        ...state.startKeys,
        all: exclusiveStartKey,
      },
    };
  });
};

const isFixedViewType = (
  v: ViewType,
): v is Exclude<ViewType, `Label_${string}`> =>
  !v.startsWith(LABEL_PREFIXES.GMAIL_LABEL);

// Combined fetch writes every item under viewTypes[0] regardless of its real
// labels, so this filter only applies to corrected items (`wasCorrected`).
// `viewTypes` is every view type actually queried by the fetch that produced
// this list, not just the one it's stored under — a combined fetch queries
// PRIMARY plus the disabled tabs, so a corrected item must match any of them.
const isRelevantForViewType = (
  communication: UserCommunication,
  viewTypes: ViewType[],
): boolean => {
  if (!communication.platform) {
    return true;
  }
  const views = calculateViews({
    labelIds: communication.labelIds,
    platform: communication.platform,
  });
  return views.some((view) => viewTypes.includes(view));
};

const filterReconciledForViewType = (
  reconciled: ReconcileCommunicationResult[],
  viewTypes: ViewType[],
): UserCommunication[] =>
  reconciled
    .filter(
      ({ communication, wasCorrected }) =>
        !wasCorrected || isRelevantForViewType(communication, viewTypes),
    )
    .map(({ communication }) => communication);

// Resolved by identity, never by a bare `intents[messageId]` lookup: the stored
// row's messageId can differ from the one the intent is keyed under, because
// removeAndInsertInState swaps the whole row on a thread WebSocket INSERT and
// the Graph API rotates messageId on folder moves.
const hasActiveLabelsIntent = (communication: UserCommunication): boolean =>
  findActiveLabelsIntentFor(
    useCommunicationIntentsStore.getState().intents,
    communication,
  ) !== undefined;

const isAlreadyInList = (
  list: UserCommunication[],
  candidate: UserCommunication,
): boolean =>
  list.some(
    (msg) =>
      msg.messageId === candidate.messageId ||
      (!!candidate.draftId && msg.draftId === candidate.draftId) ||
      (!!candidate.threadMessageId &&
        msg.threadMessageId === candidate.threadMessageId) ||
      (!!candidate.draftId && rowCarriesDraft(msg, candidate.draftId)),
  );

// A first-page payload can be stale in both directions: an item it still
// lists may already belong elsewhere (handled by filterReconciledForViewType
// above), and an item it omits may not have converged on the backend yet —
// either because of a pending label-intent, or because it's a draft whose
// save the (WebSocket-less) server hasn't reflected back yet.
const reinjectLocalOnlyRows = (
  previousList: UserCommunication[],
  newList: UserCommunication[],
  viewTypes: ViewType[],
  unconfirmedDrafts: Record<string, number>,
  now: number,
): UserCommunication[] => {
  const toReinject = previousList.filter(
    (item) =>
      (hasActiveLabelsIntent(item) ||
        isFreshUnconfirmedDraft(unconfirmedDrafts, item, now)) &&
      isRelevantForViewType(item, viewTypes) &&
      !isAlreadyInList(newList, item),
  );

  if (toReinject.length === 0) {
    return newList;
  }

  return [...newList, ...toReinject].sort((a, b) => b.createdAt - a.createdAt);
};

const DRAFT_CONTENT_FIELDS = [
  'subject',
  'messageText',
  'messageRawText',
  'messagePreview',
  'recipients',
  'attachments',
  'createdAt',
] as const satisfies readonly (keyof UserCommunication)[];

const pickDraftContentFields = (
  source: UserCommunication,
): Pick<UserCommunication, (typeof DRAFT_CONTENT_FIELDS)[number]> => {
  const picked = {} as Pick<
    UserCommunication,
    (typeof DRAFT_CONTENT_FIELDS)[number]
  >;
  DRAFT_CONTENT_FIELDS.forEach((field) => {
    (picked as Record<string, unknown>)[field] = source[field];
  });
  return picked;
};

// A refetch landing before the server learns of a just-issued delete (the
// Gmail webhook can take minutes) would otherwise resurrect the draft as the
// thread's head row — see the tombstone rule in STORE.md. Mirrors
// handleDeleteDraft's own restore logic: the draft itself is replaced by
// whatever non-draft thread content remains (dropped if none), a
// thread-carried draft is stripped in place.
const reconcileDeletedDraftRow = (
  serverRow: UserCommunication,
  draftId: string,
): UserCommunication | null =>
  serverRow.draftId === draftId
    ? buildThreadRestoreUserCommunication(serverRow, draftId)
    : stripThreadCarriedDraft(serverRow, draftId);

// Same tombstone rule reconcileUnconfirmedDrafts applies to a batched
// refetch, but for the single-row insert paths (WS INSERT, useGetUserCommunicationById,
// useInboxSearch, useMoveToFolder, DoBox card open, …) — none of which go through
// reconcileUnconfirmedDrafts, so a tombstoned draft would otherwise resurrect the
// thread's draft head straight back into view. Expired tombstones are treated as
// absent (the row passes through unchanged) without touching state.deletedDrafts —
// pruning that map is reconcileUnconfirmedDrafts' job, on the next batched save.
type DraftTombstoneOutcome = 'unchanged' | 'restored' | 'stripped' | 'dropped';

const applyDeletedDraftTombstone = (
  row: UserCommunication,
  state: UserCommunicationsStore,
  now: number,
): { row: UserCommunication | null; outcome: DraftTombstoneOutcome } => {
  const draftId = resolveServerRowDraftId(row);
  const deletedAt = draftId ? state.deletedDrafts[draftId] : undefined;

  if (
    !draftId ||
    deletedAt === undefined ||
    now - deletedAt > UNCONFIRMED_DRAFT_TTL_MS
  ) {
    return { row, outcome: 'unchanged' };
  }

  const reconciled = reconcileDeletedDraftRow(row, draftId);

  return {
    row: reconciled,
    outcome:
      row.draftId === draftId
        ? reconciled
          ? 'restored'
          : 'dropped'
        : 'stripped',
  };
};

// A fresh reply/forward draft is already the thread's head row locally (see
// handleUpsertDraft), but has no server-side link until ingestion completes —
// until then a refetch still returns the pre-draft original as the thread's
// head. Finds every such local draft head still in the ledger.
const findFreshThreadDraftHeads = (
  state: UserCommunicationsStore,
  unconfirmedDrafts: Record<string, number>,
  now: number,
): UserCommunication[] =>
  Object.keys(unconfirmedDrafts)
    .filter((draftId) =>
      isFreshUnconfirmedDraft(unconfirmedDrafts, { draftId }, now),
    )
    .map((draftId) =>
      findUserCommunicationInAllCategories(
        () => state,
        (msg) => msg.draftId === draftId && !!msg.threadMessageId,
      ),
    )
    .filter((row): row is UserCommunication => !!row);

// Swaps a payload row for the local draft head of the same thread when the
// payload doesn't carry the draft yet (pre-ingestion) — otherwise the
// still-server-side original renders next to, or instead of, the local draft.
// Runs before the draftId-keyed rules below and before reinjectLocalOnlyRows,
// so the same draft is never substituted once and reinjected again
// (isAlreadyInList already matches this row by threadMessageId).
const substituteThreadHeadDrafts = (
  incoming: UserCommunication[],
  state: UserCommunicationsStore,
  unconfirmedDrafts: Record<string, number>,
  now: number,
): UserCommunication[] => {
  const freshThreadDraftHeads = findFreshThreadDraftHeads(
    state,
    unconfirmedDrafts,
    now,
  );

  if (freshThreadDraftHeads.length === 0) {
    return incoming;
  }

  return incoming.map((serverRow) => {
    const localHead = freshThreadDraftHeads.find(
      (draftHead) =>
        !rowCarriesDraft(serverRow, draftHead.draftId!) &&
        (serverRow.threadMessageId === draftHead.threadMessageId ||
          serverRow.messageId === draftHead.threadMessageId),
    );
    return localHead ?? serverRow;
  });
};

// Confirms an unconfirmed draft only once the server's row is CLEARLY newer
// than the local save (a genuine external edit, e.g. from Gmail web) or the
// TTL has expired, else keeps the local row exactly (still unconfirmed) so a
// stale server value never clobbers a save it hasn't caught up to yet.
const reconcileUnconfirmedDrafts = (
  incoming: UserCommunication[],
  state: UserCommunicationsStore,
  now: number,
): {
  communications: UserCommunication[];
  unconfirmedDrafts: Record<string, number>;
  deletedDrafts: Record<string, number>;
  lastDeletedDraftSliceKeys: Record<string, string[]>;
} => {
  // Pruned up front, not just for drafts this payload happens to carry: a
  // TTL-expired mark is stale truth regardless of which view refetches next,
  // so any save call clears it and lets the (present-or-absent) server win.
  let unconfirmedDrafts = Object.fromEntries(
    Object.entries(state.unconfirmedDrafts).filter(
      ([, savedAt]) => now - savedAt <= UNCONFIRMED_DRAFT_TTL_MS,
    ),
  );

  // Same TTL as unconfirmedDrafts — past it the server (present or absent)
  // becomes the truth again, same as any other unconfirmed local write.
  const deletedDrafts = Object.fromEntries(
    Object.entries(state.deletedDrafts).filter(
      ([, deletedAt]) => now - deletedAt <= UNCONFIRMED_DRAFT_TTL_MS,
    ),
  );

  // Piggybacks on deletedDrafts' own TTL rather than carrying a second
  // timestamp: an entry only survives here as long as its tombstone does.
  const lastDeletedDraftSliceKeys = Object.fromEntries(
    Object.entries(state.lastDeletedDraftSliceKeys).filter(
      ([draftId]) => draftId in deletedDrafts,
    ),
  );

  const threadSubstituted = substituteThreadHeadDrafts(
    incoming,
    state,
    unconfirmedDrafts,
    now,
  );

  const communications = threadSubstituted
    .map((serverRow) => {
      const draftId = resolveServerRowDraftId(serverRow);

      if (draftId && draftId in deletedDrafts) {
        return reconcileDeletedDraftRow(serverRow, draftId);
      }

      if (!draftId || !(draftId in unconfirmedDrafts)) {
        return serverRow;
      }

      const localSavedAt = unconfirmedDrafts[draftId];

      if (serverRow.createdAt > localSavedAt + CONFIRMATION_CLOCK_SKEW_MS) {
        const { [draftId]: _confirmed, ...remaining } = unconfirmedDrafts;
        unconfirmedDrafts = remaining;
        return serverRow;
      }

      // Our own echo row is never "clearly newer" than the local save that
      // produced it (see CONFIRMATION_CLOCK_SKEW_MS), so the mark is only ever
      // cleared by a genuine external edit or by TTL expiry — intended, not a gap.
      const localRow = findUserCommunicationInAllCategories(
        () => state,
        (msg) => msg.draftId === draftId,
      );

      if (serverRow.draftId === draftId) {
        return localRow ?? serverRow;
      }

      // The draft is thread-carried: the server's thread head is a genuine
      // newer non-draft reply and must remain the row, or that reply vanishes
      // from every view until the TTL. Only the nested draft entry is refreshed.
      if (!localRow) {
        return serverRow;
      }

      const localDraftContent = pickDraftContentFields(localRow);
      return {
        ...serverRow,
        threadMessages: serverRow.threadMessages?.map((message) =>
          message.draftId === draftId
            ? { ...message, ...localDraftContent }
            : message,
        ),
      };
    })
    .filter((row): row is UserCommunication => row !== null);

  return {
    communications,
    unconfirmedDrafts,
    deletedDrafts,
    lastDeletedDraftSliceKeys,
  };
};

// The list's exclusiveStartKey embeds the thread's latest message date, so a
// draft save shifts pagination and a loadMore page can repeat rows already
// present — dedupe against currentList using the same matching semantics as
// isAlreadyInList, keeping the existing row on any collision.
const buildMergedList = (
  currentList: UserCommunication[],
  incoming: UserCommunication[],
  relevantViewTypes: ViewType[],
  unconfirmedDrafts: Record<string, number>,
  now: number,
  loadMore?: boolean,
): UserCommunication[] =>
  loadMore
    ? [
        ...currentList,
        ...incoming.filter((item) => !isAlreadyInList(currentList, item)),
      ]
    : reinjectLocalOnlyRows(
        currentList,
        incoming,
        relevantViewTypes,
        unconfirmedDrafts,
        now,
      );

const applySaveIndividualUserCommunications = (
  state: UserCommunicationsStore,
  reconciledCommunications: ReconcileCommunicationResult[],
  unconfirmedDrafts: Record<string, number>,
  deletedDrafts: Record<string, number>,
  lastDeletedDraftSliceKeys: Record<string, string[]>,
  exclusiveStartKey: string | null,
  viewType: ViewType,
  relevantViewTypes: ViewType[],
  now: number,
  loadMore?: boolean,
): Partial<UserCommunicationsStore> => {
  const updatedState: Partial<UserCommunicationsStore> = {
    unconfirmedDrafts,
    deletedDrafts,
    lastDeletedDraftSliceKeys,
  };

  if (viewType.startsWith(LABEL_PREFIXES.GMAIL_LABEL)) {
    const labelId = labelStateKey(viewType);
    const relevantCommunications = filterReconciledForViewType(
      reconciledCommunications,
      relevantViewTypes,
    );
    const previousList = state.labels[labelId] || [];
    const mergedList = buildMergedList(
      previousList,
      relevantCommunications,
      relevantViewTypes,
      unconfirmedDrafts,
      now,
      loadMore,
    );
    updatedState.startKeys = {
      ...state.startKeys,
      labels: { ...state.startKeys.labels, [labelId]: exclusiveStartKey },
    };
    updatedState.labels = { ...state.labels, [labelId]: mergedList };
  } else if (isFixedViewType(viewType)) {
    const key = viewTypeToStateKey(viewType);
    const relevantCommunications = filterReconciledForViewType(
      reconciledCommunications,
      relevantViewTypes,
    );
    const currentList = Array.isArray(state[key])
      ? (state[key] as UserCommunication[])
      : [];
    const mergedList = buildMergedList(
      currentList,
      relevantCommunications,
      relevantViewTypes,
      unconfirmedDrafts,
      now,
      loadMore,
    );
    updatedState.startKeys = { ...state.startKeys, [key]: exclusiveStartKey };
    (updatedState as any)[key] = mergedList;
  } else {
    // Outlook folder - use viewType directly as key in labels. Not modeled
    // by calculateViews, so every reconciled item passes through
    // unfiltered — matches the pre-ledger behavior for this branch.
    const passthroughCommunications = reconciledCommunications.map(
      ({ communication }) => communication,
    );
    const previousList = state.labels[viewType] || [];
    const mergedList = buildMergedList(
      previousList,
      passthroughCommunications,
      relevantViewTypes,
      unconfirmedDrafts,
      now,
      loadMore,
    );
    updatedState.startKeys = {
      ...state.startKeys,
      labels: { ...state.startKeys.labels, [viewType]: exclusiveStartKey },
    };
    updatedState.labels = { ...state.labels, [viewType]: mergedList };
  }

  return updatedState;
};

export const handleSaveIndividualUserCommunications = (
  set: ZustandSet,
  userCommunications: UserCommunication[],
  exclusiveStartKey: string | null,
  viewType: ViewType,
  loadMore?: boolean,
  queriedViewTypes?: ViewType[],
) => {
  const relevantViewTypes = queriedViewTypes ?? [viewType];
  const now = Date.now();
  const { isEmailAccountRemoved } = useIntegrationsStore.getState();
  const rows = userCommunications.filter(
    (row) => !isEmailRowOfRemovedAccount(isEmailAccountRemoved)(row),
  );

  set((state) => {
    const {
      communications: draftReconciled,
      unconfirmedDrafts,
      deletedDrafts,
      lastDeletedDraftSliceKeys,
    } = reconcileUnconfirmedDrafts(rows, state, now);

    const reconciledCommunications = draftReconciled.map((communication) =>
      useCommunicationIntentsStore
        .getState()
        .reconcileCommunication(communication),
    );

    return applySaveIndividualUserCommunications(
      state,
      reconciledCommunications,
      unconfirmedDrafts,
      deletedDrafts,
      lastDeletedDraftSliceKeys,
      exclusiveStartKey,
      viewType,
      relevantViewTypes,
      now,
      loadMore,
    );
  });
};

const removeFromAllStates = (
  state: UserCommunicationsStore,
  messageIdOrIds: string | Set<string>,
): Partial<UserCommunicationsStore> => {
  const ids =
    typeof messageIdOrIds === 'string'
      ? new Set([messageIdOrIds])
      : messageIdOrIds;
  return {
    primary: state.primary.filter((msg) => !ids.has(msg.messageId)),
    promotions: state.promotions.filter((msg) => !ids.has(msg.messageId)),
    social: state.social.filter((msg) => !ids.has(msg.messageId)),
    updates: state.updates.filter((msg) => !ids.has(msg.messageId)),
    forums: state.forums.filter((msg) => !ids.has(msg.messageId)),
    draft: state.draft.filter((msg) => !ids.has(msg.messageId)),
    sent: state.sent.filter((msg) => !ids.has(msg.messageId)),
    spam: state.spam.filter((msg) => !ids.has(msg.messageId)),
    trash: state.trash.filter((msg) => !ids.has(msg.messageId)),
    labels: Object.fromEntries(
      Object.entries(state.labels).map(([key, communications]) => [
        key,
        communications.filter((msg) => !ids.has(msg.messageId)),
      ]),
    ),
    all: state.all.filter((msg) => !ids.has(msg.messageId)),
  };
};

export const handleDeleteUserCommunication = async (
  set: ZustandSet,
  messageId: string,
) => {
  await indexedDBAdapter.markAsDeleted(messageId, 'user-communication');

  set((state) => removeFromAllStates(state, messageId));
};

export const handleArchiveUserCommunication = (
  set: ZustandSet,
  messageId: string,
) => {
  set((state) => removeFromAllStates(state, messageId));
};

const updateCommunicationInState = (
  communications: UserCommunication[],
  messageId: string,
  updater: (msg: UserCommunication) => UserCommunication,
): UserCommunication[] => {
  return communications.map((msg) =>
    msg.messageId === messageId ? updater(msg) : msg,
  );
};

const patchCommunicationInAllSlices = (
  state: UserCommunicationsStore,
  messageId: string,
  updater: (msg: UserCommunication) => UserCommunication,
): Partial<UserCommunicationsStore> => ({
  primary: updateCommunicationInState(state.primary, messageId, updater),
  promotions: updateCommunicationInState(state.promotions, messageId, updater),
  social: updateCommunicationInState(state.social, messageId, updater),
  updates: updateCommunicationInState(state.updates, messageId, updater),
  forums: updateCommunicationInState(state.forums, messageId, updater),
  draft: updateCommunicationInState(state.draft, messageId, updater),
  sent: updateCommunicationInState(state.sent, messageId, updater),
  spam: updateCommunicationInState(state.spam, messageId, updater),
  trash: updateCommunicationInState(state.trash, messageId, updater),
  labels: Object.fromEntries(
    Object.entries(state.labels).map(([key, communications]) => [
      key,
      updateCommunicationInState(communications, messageId, updater),
    ]),
  ),
  all: updateCommunicationInState(state.all, messageId, updater),
});

const handleUpdateUserCommunicationLabels = (
  set: ZustandSet,
  messageId: string,
  updateLabels: (currentLabels: LabelId[] | undefined) => LabelId[],
) => {
  set((state) =>
    patchCommunicationInAllSlices(state, messageId, (msg) => ({
      ...msg,
      labelIds: updateLabels(msg.labelIds),
    })),
  );
};

export const handleReadUserCommunication = (
  set: ZustandSet,
  messageId: string,
) => {
  handleUpdateUserCommunicationLabels(
    set,
    messageId,
    (currentLabels) =>
      currentLabels?.filter((label) => label !== LabelId.UNREAD) || [],
  );
};

export const handleUnReadUserCommunication = (
  set: ZustandSet,
  messageId: string,
) => {
  handleUpdateUserCommunicationLabels(set, messageId, (currentLabels) =>
    [...(currentLabels || []), LabelId.UNREAD].filter(
      (label, index, arr) => arr.indexOf(label) === index,
    ),
  );
};

const insertIntoState = (
  communications: UserCommunication[],
  newCommunication: UserCommunication,
): UserCommunication[] => {
  let exists;

  if (newCommunication.draftId) {
    exists = (communications || []).some(
      (msg) => msg.draftId === newCommunication.draftId,
    );
  } else {
    exists = (communications || []).some(
      (msg) => msg.messageId === newCommunication.messageId,
    );
  }

  if (exists) {
    return communications || [];
  }

  return [newCommunication, ...(communications || [])].sort(
    (a, b) => b.createdAt - a.createdAt,
  );
};

type ViewTypeSliceUpdater = (
  communications: UserCommunication[],
  item: UserCommunication,
) => UserCommunication[];

// Shared by handleInsertUserCommunication and handleRemoveAndInsertUserCommunication —
// both route a single row into every fixed/label slice calculateViews resolves it to,
// differing only in how they reconcile against an existing row in that slice.
const applyAcrossViewTypes = (
  state: UserCommunicationsStore,
  item: UserCommunication,
  updateSlice: ViewTypeSliceUpdater,
): Partial<UserCommunicationsStore> => {
  const viewTypes = calculateViews({
    labelIds: item.labelIds,
    platform: item.platform!,
  });
  const newState: Partial<UserCommunicationsStore> = {
    primary: state.primary,
    promotions: state.promotions,
    social: state.social,
    updates: state.updates,
    forums: state.forums,
    draft: state.draft,
    sent: state.sent,
    spam: state.spam,
    trash: state.trash,
    labels: { ...state.labels },
    all: state.all,
  };

  viewTypes.forEach((viewType) => {
    if (viewType.startsWith(LABEL_PREFIXES.GMAIL_LABEL)) {
      const labelKey = labelStateKey(viewType);
      newState.labels![labelKey] = updateSlice(
        newState.labels![labelKey] ?? [],
        item,
      );
    } else {
      const key = viewTypeToStateKey(
        viewType as Exclude<ViewType, `Label_${string}`>,
      );
      newState[key] = updateSlice(state[key], item);
    }
  });

  return newState;
};

export const handleInsertUserCommunication = (
  set: ZustandSet,
  userCommunication: UserCommunication,
) => {
  const passedFilter = shouldFilterCommunication(userCommunication);
  if (!passedFilter) {
    return;
  }

  set((state) => {
    const { row: resolved } = applyDeletedDraftTombstone(
      userCommunication,
      state,
      Date.now(),
    );
    return resolved
      ? applyAcrossViewTypes(state, resolved, insertIntoState)
      : state;
  });
};

export const handleModifyUserCommunication = (
  set: ZustandSet,
  updatedUserCommunication: UserCommunication,
) => {
  set((state) => {
    const shouldRemoveFromInbox = (msg: UserCommunication) => {
      const labels = msg.labelIds ?? [];

      return (
        !labels.includes(LabelId.INBOX) ||
        labels.includes(LabelId.SPAM) ||
        labels.includes(LabelId.TRASH)
      );
    };

    const updater = (msg: UserCommunication) =>
      msg.messageId === updatedUserCommunication.messageId
        ? { ...msg, ...updatedUserCommunication }
        : msg;

    const removeTargetIfLeavingInbox = (msg: UserCommunication) =>
      msg.messageId === updatedUserCommunication.messageId &&
      shouldRemoveFromInbox(msg);

    const updateInboxSlice = (communications: UserCommunication[]) =>
      communications
        .map(updater)
        .filter((msg) => !removeTargetIfLeavingInbox(msg));

    return {
      primary: updateInboxSlice(state.primary),
      promotions: updateInboxSlice(state.promotions),
      social: updateInboxSlice(state.social),
      updates: updateInboxSlice(state.updates),
      forums: updateInboxSlice(state.forums),
      draft: state.draft.map(updater),
      sent: state.sent.map(updater),
      spam: state.spam.map(updater),
      trash: state.trash.map(updater),
      labels: Object.fromEntries(
        Object.entries(state.labels).map(([key, communications]) => [
          key,
          communications.map(updater),
        ]),
      ),
      all: state.all.map(updater),
    };
  });
};

// Content-only patch (messageRawText/messageText/attachments) for a fetch that
// hydrates a row's body from S3. Deliberately narrower than
// handleModifyUserCommunication: it never spreads a caller-supplied
// UserCommunication snapshot, so a stale fetch can never carry recipients,
// labels or any other field backward over a fresher local edit.
export const handleUpdateUserCommunicationContentFields = (
  set: ZustandSet,
  messageId: string,
  fields: Pick<
    UserCommunication,
    'messageRawText' | 'messageText' | 'attachments'
  >,
) => {
  set((state) => {
    const updater = (msg: UserCommunication) =>
      msg.messageId === messageId ? { ...msg, ...fields } : msg;

    return {
      primary: state.primary.map(updater),
      promotions: state.promotions.map(updater),
      social: state.social.map(updater),
      updates: state.updates.map(updater),
      forums: state.forums.map(updater),
      draft: state.draft.map(updater),
      sent: state.sent.map(updater),
      spam: state.spam.map(updater),
      trash: state.trash.map(updater),
      labels: Object.fromEntries(
        Object.entries(state.labels).map(([key, communications]) => [
          key,
          communications.map(updater),
        ]),
      ),
      all: state.all.map(updater),
    };
  });
};

export const handleModifyUserCommunicationByThreadMessageId = (
  set: ZustandSet,
  updatedUserCommunication: UserCommunication,
) => {
  set((state) => {
    const updater = (msg: UserCommunication) =>
      msg.threadMessageId === updatedUserCommunication.threadMessageId
        ? { ...msg, ...updatedUserCommunication }
        : msg;

    return {
      primary: state.primary.map(updater),
      promotions: state.promotions.map(updater),
      social: state.social.map(updater),
      updates: state.updates.map(updater),
      forums: state.forums.map(updater),
      draft: state.draft.map(updater),
      sent: state.sent.map(updater),
      spam: state.spam.map(updater),
      trash: state.trash.map(updater),
      labels: Object.fromEntries(
        Object.entries(state.labels).map(([key, communications]) => [
          key,
          communications.map(updater),
        ]),
      ),
      all: state.all.map(updater),
    };
  });
};

// A WebSocket REMOVE only fires once the server learns of a deletion — for a
// draft that can be minutes after the local delete already restored the
// thread (see handleDeleteDraft). If the removed row was a draft thread head
// and carried other non-draft messages nested in threadMessages, a plain
// removal would drop the whole thread instead of restoring it, so the
// richest known copy is checked for restorable content before removing.
// A REMOVE for a non-draft row (a thread leaving the Inbox because it was
// linked to a DoBox entry, or a real message deletion) must drop the whole
// row as before — restoring it would put the thread right back in view.
export const handleRemoveUserCommunicationByMessageId = (
  set: ZustandSet,
  get: ZustandGet,
  messageId: string,
): void => {
  const matches = (msg: UserCommunication): boolean =>
    msg.messageId === messageId;

  void indexedDBAdapter.markAsDeleted(messageId, 'user-communication');

  const removedRow = findRichestUserCommunicationAcrossCategories(
    get(),
    matches,
  );

  // The tombstone above must be written regardless — it blocks a later
  // arrival (an INSERT that raced this REMOVE, or an eventually-consistent
  // by-id read) from resurrecting a message this store never held. Only the
  // state write (and the resulting persist/re-render) is skipped here: zustand
  // v5 only skips subscriber notification when `set` is never called.
  if (!removedRow) {
    return;
  }

  set((state) => {
    if (!isDraft(removedRow)) {
      return removeFromAllStates(state, messageId);
    }

    const replacement = buildThreadRestoreUserCommunication(
      removedRow,
      messageId,
    );
    const stripDraftId = resolveServerRowDraftId(removedRow) ?? messageId;
    const { newState } = replaceRowInSlices(
      state,
      matches,
      stripDraftId,
      replacement,
    );

    return newState;
  });
};

interface HeldRemoval {
  messageId: string;
  richestRow: UserCommunication;
}

// Single pass over every slice, restricted to the batch's ids, building the
// same "richest = longest threadMessages, first seen wins on a tie" result
// findRichestUserCommunicationAcrossCategories computes per id — but without
// re-scanning every category and label list once per id (resolveHeldRemovals'
// caller can pass in the hundreds).
const collectRichestByMessageId = (
  state: UserCommunicationsStore,
  ids: Set<string>,
): Map<string, UserCommunication> => {
  const richestById = new Map<string, UserCommunication>();

  const consider = (row: UserCommunication) => {
    if (!ids.has(row.messageId)) {
      return;
    }
    const existing = richestById.get(row.messageId);
    const rowThreadLength = row.threadMessages?.length ?? 0;
    const existingThreadLength = existing?.threadMessages?.length ?? 0;
    if (!existing || rowThreadLength > existingThreadLength) {
      richestById.set(row.messageId, row);
    }
  };

  FIXED_CATEGORIES.forEach((category) => {
    (state[category] as UserCommunication[] | undefined)?.forEach(consider);
  });

  if (state.labels) {
    Object.values(state.labels).forEach((items) => {
      items?.forEach(consider);
    });
  }

  return richestById;
};

const resolveHeldRemovals = (
  state: UserCommunicationsStore,
  messageIds: string[],
): HeldRemoval[] => {
  const richestById = collectRichestByMessageId(state, new Set(messageIds));

  return messageIds
    .map((messageId) => {
      const richestRow = richestById.get(messageId);
      return richestRow ? { messageId, richestRow } : undefined;
    })
    .filter((candidate): candidate is HeldRemoval => candidate !== undefined);
};

const mergeSliceState = (
  state: UserCommunicationsStore,
  partial: Partial<UserCommunicationsStore>,
): UserCommunicationsStore => ({ ...state, ...partial });

// Non-draft ids drop in one Set-filtered pass; draft ids each keep
// handleRemoveUserCommunicationByMessageId's restore-in-place branch, applied
// in sequence so a later draft's restore sees an earlier one's slice changes.
const applyBatchRemoval = (
  state: UserCommunicationsStore,
  held: HeldRemoval[],
): Partial<UserCommunicationsStore> => {
  const draftRemovals = held.filter(({ richestRow }) => isDraft(richestRow));
  const plainIds = new Set(
    held
      .filter(({ richestRow }) => !isDraft(richestRow))
      .map(({ messageId }) => messageId),
  );

  let result: Partial<UserCommunicationsStore> = removeFromAllStates(
    state,
    plainIds,
  );

  draftRemovals.forEach(({ messageId, richestRow }) => {
    const replacement = buildThreadRestoreUserCommunication(
      richestRow,
      messageId,
    );
    const stripDraftId = resolveServerRowDraftId(richestRow) ?? messageId;
    const { newState } = replaceRowInSlices(
      mergeSliceState(state, result),
      (msg) => msg.messageId === messageId,
      stripDraftId,
      replacement,
    );
    result = { ...result, ...newState };
  });

  return result;
};

// REMOVE_BATCH is sent after the same invocation's inline INSERTs, so every id gets a tombstone, held or not.
export const handleRemoveUserCommunicationsByMessageIds = (
  set: ZustandSet,
  get: ZustandGet,
  messageIds: string[],
): void => {
  if (messageIds.length === 0) {
    return;
  }

  void indexedDBAdapter.markManyAsDeleted(
    messageIds.map((id) => ({
      id,
      entityType: 'user-communication' as const,
    })),
  );

  const held = resolveHeldRemovals(get(), messageIds);
  if (held.length === 0) {
    return;
  }

  set((state) => applyBatchRemoval(state, held));
};

// A newer non-draft message arriving for a thread whose head is still a
// fresh, unconfirmed local draft (see STORE.md "unconfirmed drafts") must not
// silently drop that draft — it's carried into the new head's threadMessages
// instead, the same shape a reload would show once the draft is nested under
// a newer reply.
const carryDraftIntoThreadMessages = (
  newCommunication: UserCommunication,
  draftHead: UserCommunication,
): UserCommunication => {
  const existingThreadMessages = newCommunication.threadMessages ?? [];
  const alreadyCarried = existingThreadMessages.some(
    (msg) => msg.draftId === draftHead.draftId,
  );

  return {
    ...newCommunication,
    threadMessages: alreadyCarried
      ? existingThreadMessages
      : [draftHead, ...existingThreadMessages],
  };
};

const replaceCommunicationAt = (
  communications: UserCommunication[],
  index: number,
  replacement: UserCommunication,
): UserCommunication[] =>
  communications
    .map((msg, i) => (i === index ? replacement : msg))
    .sort((a, b) => b.createdAt - a.createdAt);

const removeAndInsertInState = (
  communications: UserCommunication[],
  newCommunication: UserCommunication,
  unconfirmedDrafts: Record<string, number>,
  now: number,
): UserCommunication[] => {
  if (!communications?.length) {
    return [newCommunication];
  }

  const isDraft = newCommunication.labelIds?.includes(LabelId.DRAFT);

  const existingIndex = communications.findIndex((msg) => {
    if (isDraft && newCommunication.draftId && msg.draftId) {
      return msg.draftId === newCommunication.draftId;
    }
    return msg.threadMessageId === newCommunication.threadMessageId;
  });

  if (existingIndex === -1) {
    return [newCommunication, ...communications].sort(
      (a, b) => b.createdAt - a.createdAt,
    );
  }

  const existingMessage = communications[existingIndex];

  if (
    !isDraft &&
    isFreshUnconfirmedDraft(unconfirmedDrafts, existingMessage, now) &&
    newCommunication.createdAt > existingMessage.createdAt
  ) {
    return replaceCommunicationAt(
      communications,
      existingIndex,
      carryDraftIntoThreadMessages(newCommunication, existingMessage),
    );
  }

  if (existingMessage.createdAt > newCommunication.createdAt) {
    return communications;
  }

  const filteredCommunications = communications.filter((msg) => {
    if (isDraft && newCommunication.draftId && msg.draftId) {
      return msg.draftId !== newCommunication.draftId;
    }
    return msg.threadMessageId !== newCommunication.threadMessageId;
  });

  return [newCommunication, ...filteredCommunications].sort(
    (a, b) => b.createdAt - a.createdAt,
  );
};

export const handleRemoveAndInsertUserCommunication = (
  set: ZustandSet,
  userCommunication: UserCommunication,
) => {
  const passedFilter = shouldFilterCommunication(userCommunication);
  if (!passedFilter) {
    return;
  }
  set((state) => {
    const now = Date.now();
    const { row: resolved } = applyDeletedDraftTombstone(
      userCommunication,
      state,
      now,
    );
    return resolved
      ? applyAcrossViewTypes(state, resolved, (comms, item) =>
          removeAndInsertInState(comms, item, state.unconfirmedDrafts, now),
        )
      : state;
  });
};

// Unlike handleRemoveAndInsertUserCommunication (which routes by
// calculateViews), this inserts into exactly the given slice keys — the
// fallback path useDeleteDraft's own thread-fetch restore uses once it has
// the sliceKeys handleDeleteDraft recorded, so a server-fetched replacement
// lands where the deleted draft actually was, not wherever its own labels
// would route it. Never inserted into the `draft` slice for a non-draft row,
// same invariant replaceRowInSlices enforces.
export const handleInsertUserCommunicationIntoSlices = (
  set: ZustandSet,
  userCommunication: UserCommunication,
  sliceKeys: string[],
) => {
  const passedFilter = shouldFilterCommunication(userCommunication);
  if (!passedFilter) {
    return;
  }

  set((state) => {
    const now = Date.now();
    const { row: resolved } = applyDeletedDraftTombstone(
      userCommunication,
      state,
      now,
    );
    if (!resolved) {
      return state;
    }

    const newState: Partial<UserCommunicationsStore> = {};
    let labels: Record<string, UserCommunication[]> | undefined;

    sliceKeys.forEach((sliceKey) => {
      if (sliceKey === 'draft' && !isDraft(resolved)) {
        return;
      }

      if (sliceKey.startsWith(LABEL_SLICE_KEY_PREFIX)) {
        const labelKey = sliceKey.slice(LABEL_SLICE_KEY_PREFIX.length);
        labels = labels ?? { ...state.labels };
        labels[labelKey] = removeAndInsertInState(
          labels[labelKey] ?? [],
          resolved,
          state.unconfirmedDrafts,
          now,
        );
        return;
      }

      const category = sliceKey as keyof UserCommunicationsStore;
      (newState as Record<string, unknown>)[category] = removeAndInsertInState(
        state[category] as UserCommunication[],
        resolved,
        state.unconfirmedDrafts,
        now,
      );
    });

    if (labels) {
      newState.labels = labels;
    }

    return newState;
  });
};

// A restored row is never inserted anywhere calculateViews would route it —
// the row's OWN slice membership before the delete (the server's view
// assignment) is the only source of truth, so the draft row found in
// ['all','primary','draft'] restores into exactly those three, never into a
// 'updates'/'promotions'/etc slice the row never occupied. Shared by
// handleDeleteDraft and handleRemoveUserCommunicationByMessageId's WS-REMOVE
// draft case.
const isSameRestoredRow = (
  row: UserCommunication,
  replacement: UserCommunication,
): boolean =>
  row.messageId === replacement.messageId ||
  (!!replacement.threadMessageId &&
    row.threadMessageId === replacement.threadMessageId);

// Restores (or drops) the single matching row of one slice, at its own index,
// and strips every other row's thread-carried copy of the same draft. Never
// inserted into the `draft` slice unless the replacement is itself a draft
// (buildThreadRestoreUserCommunication never returns one) — a restored
// non-draft head does not belong there. Deduped against the rest of the same
// list by messageId/threadMessageId: a leftover from old, pre-thread-head-model
// persisted state where the original was still physically present as its own
// row keeps that existing row instead of inserting a second copy.
const restoreMatchInList = (
  list: UserCommunication[],
  sliceKey: string,
  stripDraftId: string,
  matches: (msg: UserCommunication) => boolean,
  replacement: UserCommunication | null,
): { list: UserCommunication[]; matched: boolean } => {
  const matchIndex = list.findIndex(matches);
  if (matchIndex === -1) {
    return {
      list: list.map((msg) => stripThreadCarriedDraft(msg, stripDraftId)),
      matched: false,
    };
  }

  const isDraftSlice = sliceKey === 'draft';
  const alreadyPresentElsewhere =
    !!replacement &&
    list.some(
      (msg, index) =>
        index !== matchIndex && isSameRestoredRow(msg, replacement),
    );
  const shouldDrop =
    !replacement ||
    (isDraftSlice && !isDraft(replacement)) ||
    alreadyPresentElsewhere;

  const result = list.reduce<UserCommunication[]>((acc, msg, index) => {
    if (index === matchIndex) {
      if (!shouldDrop) {
        acc.push(replacement as UserCommunication);
      }
      return acc;
    }
    acc.push(stripThreadCarriedDraft(msg, stripDraftId));
    return acc;
  }, []);

  return { list: result, matched: true };
};

// For every fixed category and every labels[key] list: replaces the row
// `matches` finds with `replacement` in place (or drops it, per
// restoreMatchInList), and returns the slice keys the row was actually found
// in — the caller's contract for "where to restore" and "where to tell a
// later fallback insert to land".
const replaceRowInSlices = (
  state: UserCommunicationsStore,
  matches: (msg: UserCommunication) => boolean,
  stripDraftId: string,
  replacement: UserCommunication | null,
): { newState: Partial<UserCommunicationsStore>; sliceKeys: string[] } => {
  const sliceKeys: string[] = [];
  const newState: Partial<UserCommunicationsStore> = {};

  FIXED_CATEGORIES.forEach((category) => {
    const { list, matched } = restoreMatchInList(
      state[category] as UserCommunication[],
      category as string,
      stripDraftId,
      matches,
      replacement,
    );
    if (matched) {
      sliceKeys.push(category as string);
    }
    (newState as Record<string, unknown>)[category as string] = list;
  });

  const labels: Record<string, UserCommunication[]> = {};
  Object.entries(state.labels).forEach(([key, communications]) => {
    const { list, matched } = restoreMatchInList(
      communications,
      `${LABEL_SLICE_KEY_PREFIX}${key}`,
      stripDraftId,
      matches,
      replacement,
    );
    if (matched) {
      sliceKeys.push(`${LABEL_SLICE_KEY_PREFIX}${key}`);
    }
    labels[key] = list;
  });
  newState.labels = labels;

  return { newState, sliceKeys };
};

export const handleDeleteDraft = (
  set: ZustandSet,
  draftId: string,
): { restoredInPlace: boolean; sliceKeys: string[] } => {
  let replacement: UserCommunication | null = null;
  let deletedRowMessageId: string | undefined;
  let sliceKeys: string[] = [];
  const deletedAt = Date.now();

  set((state) => {
    const matches = (msg: UserCommunication): boolean =>
      msg.draftId === draftId || msg.messageId === draftId;

    const draftToBeDeleted = findRichestUserCommunicationAcrossCategories(
      state,
      matches,
    );

    deletedRowMessageId = draftToBeDeleted?.messageId;

    if (draftToBeDeleted) {
      replacement = buildThreadRestoreUserCommunication(
        draftToBeDeleted,
        draftId,
      );
    }

    const { newState: restoredSlices, sliceKeys: foundSliceKeys } =
      replaceRowInSlices(state, matches, draftId, replacement);
    sliceKeys = foundSliceKeys;

    const newState: Partial<UserCommunicationsStore> = {
      ...restoredSlices,
      unconfirmedDrafts: Object.fromEntries(
        Object.entries(state.unconfirmedDrafts).filter(
          ([id]) => id !== draftId,
        ),
      ),
      // Blocks a refetch straggling in before the server learns of this
      // delete (the Gmail webhook can take minutes) from resurrecting the
      // draft as a thread's head row — see reconcileUnconfirmedDrafts.
      deletedDrafts: { ...state.deletedDrafts, [draftId]: deletedAt },
      // Read once by useDeleteDraft's own fallback restore so it inserts into
      // the same slices this pass just found, instead of recomputing
      // membership from the restored row's own labels. TTL-pruned alongside
      // deletedDrafts (see reconcileUnconfirmedDrafts/handleResetStateForFilterChange).
      lastDeletedDraftSliceKeys: {
        ...state.lastDeletedDraftSliceKeys,
        [draftId]: sliceKeys,
      },
    };

    return newState;
  });

  // Mirrors handleRemoveUserCommunicationByMessageId's own tombstone write —
  // marks both ids so useGetUserCommunicationById's recovery effect treats
  // either as a confirmed local delete instead of refetching the still-live
  // server draft when its own page is open at the moment of deletion.
  void indexedDBAdapter.markAsDeleted(draftId, 'user-communication');
  if (deletedRowMessageId && deletedRowMessageId !== draftId) {
    void indexedDBAdapter.markAsDeleted(
      deletedRowMessageId,
      'user-communication',
    );
  }

  const restoredInPlace = replacement !== null;

  return { restoredInPlace, sliceKeys };
};

const buildNewDraftCommunication = (
  draftData: Partial<UserCommunication>,
  inboxEntryId: string | undefined,
): UserCommunication =>
  ({
    messageId: draftData.messageId || '',
    draftId: draftData.draftId || '',
    subject: draftData.subject || '',
    messageText: draftData.messageText || '',
    attachments: draftData.attachments || [],
    labelIds: draftData.labelIds,
    recipients: draftData.recipients || [],
    createdAt: draftData.createdAt || Date.now(),
    author: draftData.author || '',
    authorEmail: draftData.authorEmail || '',
    inboxEntryIds: inboxEntryId ? [inboxEntryId] : undefined,
    ...draftData,
    messageRawText: draftData.messageRawText ?? draftData.messageText ?? '',
  }) as UserCommunication;

const buildUpdatedDraftCommunication = (
  existingDraft: UserCommunication,
  draftData: Partial<UserCommunication>,
): UserCommunication =>
  ({
    ...existingDraft,
    ...draftData,
    // The row's messageId is its identity (e.g. the original Gmail
    // message id for a server-originated draft) — never let a save echo
    // overwrite it with draftId, or a later re-init keyed on messageId
    // change snaps the editor back to stale content.
    messageId: existingDraft.messageId,
    messageRawText:
      draftData.messageRawText ?? existingDraft.messageRawText ?? '',
  }) as UserCommunication;

// A brand-new compose has no thread to become the head of — same
// insert-across-calculated-views behavior the store has always had for it.
const insertNewComposeDraft = (
  state: UserCommunicationsStore,
  draftCommunication: UserCommunication,
): Partial<UserCommunicationsStore> => {
  const viewTypes = calculateViews({
    labelIds: draftCommunication.labelIds,
    platform: draftCommunication.platform!,
  });
  const newState: Partial<UserCommunicationsStore> = {
    primary: state.primary,
    promotions: state.promotions,
    social: state.social,
    updates: state.updates,
    forums: state.forums,
    draft: state.draft,
    sent: state.sent,
    spam: state.spam,
    trash: state.trash,
    labels: { ...state.labels },
    all: state.all,
  };

  viewTypes.forEach((viewType) => {
    if (viewType.startsWith(LABEL_PREFIXES.GMAIL_LABEL)) {
      newState.labels![viewType] = insertIntoState(
        state.labels[viewType] ?? [],
        draftCommunication,
      );
    } else {
      const key = viewTypeToStateKey(
        viewType as Exclude<ViewType, `Label_${string}`>,
      );
      newState[key] = insertIntoState(state[key], draftCommunication);
    }
  });

  return newState;
};

// A brand-new reply/forward draft becomes the thread's head row — exactly the
// shape the server returns after ingestion — in every slice that already
// shows that thread (replaceRowInSlices), and is additionally prepended into
// any calculated view with no row for the thread yet (typically DRAFT and
// ALL_EMAILS), so the Drafts tab stays populated even for a thread that isn't
// loaded anywhere locally yet. See STORE.md "UserCommunications — drafts".
const insertNewThreadHeadDraft = (
  state: UserCommunicationsStore,
  draftCommunication: UserCommunication,
  messageId: string | undefined,
): Partial<UserCommunicationsStore> => {
  const matches = (msg: UserCommunication): boolean =>
    (!!draftCommunication.threadMessageId &&
      msg.threadMessageId === draftCommunication.threadMessageId) ||
    (!!messageId && msg.messageId === messageId);

  const { newState, sliceKeys: matchedSliceKeys } = replaceRowInSlices(
    state,
    matches,
    draftCommunication.draftId ?? '',
    draftCommunication,
  );
  const matchedSet = new Set(matchedSliceKeys);

  const viewTypes = calculateViews({
    labelIds: draftCommunication.labelIds,
    platform: draftCommunication.platform!,
  });

  viewTypes.forEach((viewType) => {
    if (viewType.startsWith(LABEL_PREFIXES.GMAIL_LABEL)) {
      const sliceKey = `${LABEL_SLICE_KEY_PREFIX}${viewType}`;
      if (matchedSet.has(sliceKey)) {
        return;
      }
      newState.labels![viewType] = insertIntoState(
        newState.labels![viewType] ?? [],
        draftCommunication,
      );
    } else {
      const key = viewTypeToStateKey(
        viewType as Exclude<ViewType, `Label_${string}`>,
      );
      if (matchedSet.has(key as string)) {
        return;
      }
      (newState as Record<string, unknown>)[key] = insertIntoState(
        (newState[key] as UserCommunication[] | undefined) ?? [],
        draftCommunication,
      );
    }
  });

  return newState;
};

export const handleUpsertDraft = (
  set: ZustandSet,
  draftData: Partial<UserCommunication>,
  messageId?: string,
  inboxEntryId?: string,
) => {
  set((state) => {
    const existingDraft = findUserCommunicationInAllCategories(
      () => state,
      (msg) => msg.draftId === draftData.draftId,
    );

    const draftCommunication = existingDraft
      ? buildUpdatedDraftCommunication(existingDraft, draftData)
      : buildNewDraftCommunication(draftData, inboxEntryId);

    const unconfirmedDrafts = draftCommunication.draftId
      ? {
          ...state.unconfirmedDrafts,
          [draftCommunication.draftId]: Date.now(),
        }
      : state.unconfirmedDrafts;

    // A subsequent save keeps updating the draft in place, wherever it
    // currently sits — which, for a reply/forward draft, can be a slice
    // (e.g. primary) calculateViews(draftCommunication.labelIds) would never
    // compute from the draft's own labels.
    if (existingDraft) {
      const { newState } = replaceRowInSlices(
        state,
        (msg) => msg.draftId === draftCommunication.draftId,
        draftCommunication.draftId ?? '',
        draftCommunication,
      );
      return { ...newState, unconfirmedDrafts };
    }

    const newState = draftCommunication.threadMessageId
      ? insertNewThreadHeadDraft(state, draftCommunication, messageId)
      : insertNewComposeDraft(state, draftCommunication);

    return { ...newState, unconfirmedDrafts };
  });
};

/**
 * Updates the labels of a UserCommunication. When the change does not alter
 * the views it belongs to (e.g. STARRED/IMPORTANT toggles) the row is patched
 * in place, because combined fetch can store it in a slice `calculateViews`
 * would not predict. Otherwise it sweeps the row from every slice and re-adds
 * it to the slices of the new label set.
 */
export const handleUpdateEmailLabels = (
  set: ZustandSet,
  messageId: string,
  newLabelIds: LabelId[],
) => {
  set((state) => {
    const communication = findUserCommunicationInAllCategories(
      () => state,
      (msg) => msg.messageId === messageId,
    );
    if (!communication) return state;

    const updatedCommunication = { ...communication, labelIds: newLabelIds };
    if (haveSameViews(communication, updatedCommunication)) {
      return patchCommunicationInAllSlices(
        state,
        messageId,
        () => updatedCommunication,
      );
    }
    return refileCommunication(state, messageId, updatedCommunication);
  });
};

const haveSameViews = (
  before: UserCommunication,
  after: UserCommunication,
): boolean => {
  const toViews = (comm: UserCommunication) =>
    new Set(
      calculateViews({ labelIds: comm.labelIds, platform: comm.platform! }),
    );
  const beforeViews = toViews(before);
  const afterViews = toViews(after);
  return (
    beforeViews.size === afterViews.size &&
    [...beforeViews].every((view) => afterViews.has(view))
  );
};

const insertSortedByCreatedAt = (
  communications: UserCommunication[],
  item: UserCommunication,
): UserCommunication[] =>
  [...communications, item].sort((a, b) => b.createdAt - a.createdAt);

const addToViewSlices = (
  state: Partial<UserCommunicationsStore>,
  item: UserCommunication,
): Partial<UserCommunicationsStore> => {
  const result = { ...state, labels: { ...state.labels } };
  calculateViews({ labelIds: item.labelIds, platform: item.platform! }).forEach(
    (viewType) => {
      if (viewType === VIEW_TYPES.ALL_EMAILS) return;
      if (viewType.startsWith(LABEL_PREFIXES.GMAIL_LABEL)) {
        const labelKey = labelStateKey(viewType);
        result.labels[labelKey] = insertSortedByCreatedAt(
          result.labels[labelKey] ?? [],
          item,
        );
        return;
      }
      const key = viewTypeToStateKey(
        viewType as Exclude<ViewType, `Label_${string}`>,
      );
      if (result[key]) {
        (result[key] as UserCommunication[]) = insertSortedByCreatedAt(
          result[key] as UserCommunication[],
          item,
        );
      }
    },
  );
  return result;
};

const refileCommunication = (
  state: UserCommunicationsStore,
  messageId: string,
  updatedCommunication: UserCommunication,
): Partial<UserCommunicationsStore> => {
  const swept: Partial<UserCommunicationsStore> = {
    ...removeFromAllStates(state, messageId),
    all: state.all.map((msg) =>
      msg.messageId === messageId ? updatedCommunication : msg,
    ),
  };
  return addToViewSlices(swept, updatedCommunication);
};

export const handleAddRecentlyInsertedId = (set: ZustandSet, id: string) => {
  set((state) => ({
    recentlyInsertedIds: state.recentlyInsertedIds.includes(id)
      ? state.recentlyInsertedIds
      : [...state.recentlyInsertedIds, id],
  }));
};

export const handleRemoveRecentlyInsertedId = (set: ZustandSet, id: string) => {
  set((state) => ({
    recentlyInsertedIds: state.recentlyInsertedIds.filter((i) => i !== id),
  }));
};

export const handleClearUserCommunicationsByEmail = (
  set: ZustandSet,
  userEmailAddress: string,
  platform?: MessagingPlatform,
) => {
  // No platform: matches every row for this address, same as before this
  // param existed (see DisplayAccountList.tsx's own caller).
  const isTargetRow = (communication: UserCommunication) =>
    communication.userEmailAddress === userEmailAddress &&
    (platform === undefined || communication.platform === platform);

  set((state) => {
    const filterByEmail = (communications: UserCommunication[]) =>
      communications.filter((communication) => !isTargetRow(communication));

    return {
      primary: filterByEmail(state.primary),
      promotions: filterByEmail(state.promotions),
      social: filterByEmail(state.social),
      updates: filterByEmail(state.updates),
      forums: filterByEmail(state.forums),
      draft: filterByEmail(state.draft),
      sent: filterByEmail(state.sent),
      spam: filterByEmail(state.spam),
      trash: filterByEmail(state.trash),
      labels: Object.fromEntries(
        Object.entries(state.labels).map(([key, communications]) => [
          key,
          filterByEmail(communications),
        ]),
      ),
      all: filterByEmail(state.all),
    };
  });
};

const buildResetStartKeys = (): UserCommunicationsStore['startKeys'] => ({
  primary: null,
  promotions: null,
  social: null,
  updates: null,
  forums: null,
  draft: null,
  sent: null,
  spam: null,
  trash: null,
  labels: {},
  all: null,
});

const filterAllStatesByUnconfirmedDrafts = (
  state: UserCommunicationsStore,
  now: number,
): Pick<
  UserCommunicationsStore,
  | 'primary'
  | 'promotions'
  | 'social'
  | 'updates'
  | 'forums'
  | 'draft'
  | 'sent'
  | 'spam'
  | 'trash'
  | 'labels'
  | 'all'
> => {
  const keepUnconfirmed = (communications: UserCommunication[]) =>
    communications.filter((item) =>
      isFreshUnconfirmedDraft(state.unconfirmedDrafts, item, now),
    );

  return {
    primary: keepUnconfirmed(state.primary),
    promotions: keepUnconfirmed(state.promotions),
    social: keepUnconfirmed(state.social),
    updates: keepUnconfirmed(state.updates),
    forums: keepUnconfirmed(state.forums),
    draft: keepUnconfirmed(state.draft),
    sent: keepUnconfirmed(state.sent),
    spam: keepUnconfirmed(state.spam),
    trash: keepUnconfirmed(state.trash),
    labels: Object.fromEntries(
      Object.entries(state.labels).map(([key, communications]) => [
        key,
        keepUnconfirmed(communications),
      ]),
    ),
    all: keepUnconfirmed(state.all),
  };
};

// Like resetState, but for an account-filter change: an unconfirmed draft is
// local-only truth (see reconcileUnconfirmedDrafts) and must survive the
// reset, or a save made just before switching filters is silently lost. The
// ledger itself is pruned of TTL-expired ids here too, or a later reinjection
// could resurrect an expired-and-absent draft.
export const handleResetStateForFilterChange = (set: ZustandSet) => {
  const now = Date.now();

  set((state) => {
    const deletedDrafts = Object.fromEntries(
      Object.entries(state.deletedDrafts).filter(
        ([, deletedAt]) => now - deletedAt <= UNCONFIRMED_DRAFT_TTL_MS,
      ),
    );

    return {
      ...filterAllStatesByUnconfirmedDrafts(state, now),
      unconfirmedDrafts: Object.fromEntries(
        Object.entries(state.unconfirmedDrafts).filter(
          ([, savedAt]) => now - savedAt <= UNCONFIRMED_DRAFT_TTL_MS,
        ),
      ),
      deletedDrafts,
      lastDeletedDraftSliceKeys: Object.fromEntries(
        Object.entries(state.lastDeletedDraftSliceKeys).filter(
          ([draftId]) => draftId in deletedDrafts,
        ),
      ),
      recentlyInsertedIds: [],
      syncedViewTypes: [],
      fetchGeneration: state.fetchGeneration + 1,
      isLoading: false,
      createLoading: false,
      forwardLoading: false,
      replyLoading: false,
      deleteLoading: false,
      archiveLoading: false,
      moveToInboxLoading: false,
      markAsReadLoading: false,
      markAsUnreadLoading: false,
      startKeys: buildResetStartKeys(),
    };
  });
};

const FIXED_CATEGORIES: (keyof UserCommunicationsStore)[] = [
  'all',
  'primary',
  'promotions',
  'social',
  'updates',
  'forums',
  'draft',
  'sent',
  'spam',
  'trash',
];

const findUserCommunicationInAllCategories = (
  get: ZustandGet,
  predicate: (msg: UserCommunication) => boolean,
): UserCommunication | undefined => {
  const state = get();

  for (const category of FIXED_CATEGORIES) {
    const items = state[category] as UserCommunication[];
    if (!items) {
      continue;
    }

    const found = items.find(predicate);
    if (found) {
      return found;
    }
  }

  if (state.labels) {
    for (const labelKey of Object.keys(state.labels)) {
      const items = state.labels[labelKey];
      if (!items) {
        continue;
      }

      const found = items.find(predicate);
      if (found) {
        return found;
      }
    }
  }

  return undefined;
};

// `all` is written independently by handleSaveAllUserCommunications, which
// (unlike handleSaveIndividualUserCommunications) never runs
// reconcileUnconfirmedDrafts — a background ALL_EMAILS/combined refetch can
// overwrite `all`'s draft row with the server's own threadMessages-less echo
// while `primary`/`draft` still hold the full local row. Restoring the
// absorbed source message needs the richest known copy, not whichever
// category findUserCommunicationInAllCategories happens to hit first.
const findRichestUserCommunicationAcrossCategories = (
  state: UserCommunicationsStore,
  predicate: (msg: UserCommunication) => boolean,
): UserCommunication | undefined => {
  const candidates = FIXED_CATEGORIES.map((category) =>
    (state[category] as UserCommunication[] | undefined)?.find(predicate),
  );

  if (state.labels) {
    Object.values(state.labels).forEach((items) => {
      candidates.push(items?.find(predicate));
    });
  }

  return candidates.reduce<UserCommunication | undefined>(
    (richest, candidate) => {
      if (!candidate) {
        return richest;
      }
      const candidateThreadLength = candidate.threadMessages?.length ?? 0;
      const richestThreadLength = richest?.threadMessages?.length ?? 0;
      return !richest || candidateThreadLength > richestThreadLength
        ? candidate
        : richest;
    },
    undefined,
  );
};

// Falls back to a message nested inside another row's threadMessages when no
// row carries it at the top level — the DRAFT/thread list row is always the
// thread's newest message, so an older reply the caller asks for by id can be
// nested instead (see findThreadCarriedDraft below for the draftId sibling).
const findThreadCarriedMessage = (
  get: ZustandGet,
  messageId: string,
): UserCommunication | undefined => {
  const parentRow = findUserCommunicationInAllCategories(get, (msg) =>
    rowCarriesMessage(msg, messageId),
  );
  return parentRow?.threadMessages?.find((msg) => msg.messageId === messageId);
};

export const handleGetUserCommunicationByMessageId = (
  get: ZustandGet,
  messageId: string,
): UserCommunication | undefined =>
  findUserCommunicationInAllCategories(
    get,
    (msg) => msg.messageId === messageId,
  ) ?? findThreadCarriedMessage(get, messageId);

export const handleGetUserCommunicationByThreadMessageId = (
  get: ZustandGet,
  threadMessageId: string,
): UserCommunication | undefined =>
  findUserCommunicationInAllCategories(
    get,
    (msg) => msg.threadMessageId === threadMessageId,
  );

export const handleGetMessageIdsByEmail = (
  get: ZustandGet,
  userEmailAddress: string,
  platform?: MessagingPlatform,
): string[] => {
  const state = get();
  const messageIds = new Set<string>();

  const collect = (communications: UserCommunication[] | undefined) => {
    communications?.forEach((msg) => {
      if (
        msg.userEmailAddress === userEmailAddress &&
        (platform === undefined || msg.platform === platform)
      ) {
        messageIds.add(msg.messageId);
      }
    });
  };

  FIXED_CATEGORIES.forEach((category) =>
    collect(state[category] as UserCommunication[] | undefined),
  );
  Object.values(state.labels ?? {}).forEach(collect);

  return Array.from(messageIds);
};

// Falls back to a thread-carried draftId (see rowCarriesDraft) when no row
// carries it at the top level — the DRAFT list query returns the thread's
// newest message, which can be a non-draft reply with the actual draft
// nested inside its own threadMessages.
const findThreadCarriedDraft = (
  get: ZustandGet,
  draftId: string,
): UserCommunication | undefined => {
  const parentRow = findUserCommunicationInAllCategories(get, (msg) =>
    rowCarriesDraft(msg, draftId),
  );
  return parentRow?.threadMessages?.find((msg) => msg.draftId === draftId);
};

export const handleGetUserCommunicationByDraftId = (
  get: ZustandGet,
  draftId: string,
): UserCommunication | undefined =>
  findUserCommunicationInAllCategories(get, (msg) => msg.draftId === draftId) ??
  findThreadCarriedDraft(get, draftId);

// Unlike handleGetUserCommunicationByDraftId (first match by category order,
// with `all` checked first), this returns whichever known copy carries the
// most threadMessages — the shape InboxEntries/handlers.ts needs to correctly
// re-hide a draft's replied-to parent when `all` holds a thread-less echo.
export const handleGetRichestUserCommunicationByDraftId = (
  get: ZustandGet,
  draftId: string,
): UserCommunication | undefined =>
  findRichestUserCommunicationAcrossCategories(
    get(),
    (msg) => msg.draftId === draftId,
  ) ?? findThreadCarriedDraft(get, draftId);
